import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface Profile {
  id: string;
  username: string;
  email: string;
  role: 'user' | 'admin' | 'super_admin';
  office_id: string;
  phone?: string;
  furigana?: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);
  const isLoggingOutRef = useRef(false);

  // プロフィール取得（例外を投げず、失敗時はnullを返すだけ。
  // 単発の取得失敗で強制ログアウトさせないことが重要）
  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('プロフィール取得エラー:', error.message);
        return null;
      }
      return data as Profile | null;
    } catch (error) {
      console.error('プロフィール取得例外:', error);
      return null;
    }
  }, []);

  // SupabaseがlocalStorageに保存するすべてのセッション情報を完全削除
  const clearAllSupabaseStorage = useCallback(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('sb-') ||
          key.startsWith('supabase.') ||
          key.includes('supabase')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // クリアできなくても先に進む
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          if (isMountedRef.current) {
            setUser(session.user);
          }

          // プロフィール取得完了まで loading を維持する。
          // これにより、ダッシュボード側の権限チェックが profile 未ロードのまま
          // 走って管理者を一般ユーザーと誤判定するのを防ぐ。
          fetchProfile(session.user.id).then((profileData) => {
            if (isMountedRef.current) {
              if (profileData) {
                setProfile(profileData);
              }
              setLoading(false);
            }
          });
        } else {
          if (isMountedRef.current) {
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
        }
      } catch (error: any) {
        // リフレッシュトークン切れ・不正など、セッションが腐っている場合：
        // localStorageのゴミを完全削除し、SDK側もクリーンにしてログアウト状態に戻す
        const errorMsg = error?.message || '';
        if (
          errorMsg.includes('Invalid Refresh Token') ||
          errorMsg.includes('Refresh Token Not Found') ||
          errorMsg.includes('refresh_token') ||
          errorMsg.includes('session_not_found')
        ) {
          console.warn('セッションが期限切れまたは破損しています。クリーンアップします。');
          clearAllSupabaseStorage();
          // SDK側の内部状態もリセット（失敗しても無視）
          try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* クリーンアップ失敗は無視 */ }
        } else {
          console.warn('セッション確認中に例外:', errorMsg || error);
        }

        if (isMountedRef.current) {
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // onAuthStateChangeのコールバックは同期的に保つ（内部ロックのデッドロック回避）
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMountedRef.current) return;

      if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        clearAllSupabaseStorage();
        return;
      }

      // トークン更新に失敗した場合もクリーンアップ
      if (event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user);
          fetchProfile(session.user.id).then((profileData) => {
            if (isMountedRef.current && profileData) {
              setProfile(profileData);
            }
          });
        }
        return;
      }

      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id).then((profileData) => {
          if (isMountedRef.current && profileData) {
            setProfile(profileData);
          }
        });
      }
    });

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile, clearAllSupabaseStorage]);

  const signOut = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    flushSync(() => {
      setUser(null);
      setProfile(null);
    });

    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
    clearAllSupabaseStorage();

    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/login', { replace: true });
    } else {
      window.location.href = '/login';
    }

    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  }, [clearAllSupabaseStorage]);

  return (
    <AuthContext.Provider value={{ user, profile, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}